/**

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.

  The state and the dispatcher are two separate contexts: a component that only ever
  dispatches (a sidenav link closing the drawer, say) subscribes to `useVisionUIDispatch`
  and is left alone when the layout state changes.
*/

import { createContext, useContext, useMemo, useReducer } from "react";

import type { Dispatch, ReactNode } from "react";

interface ControllerState {
  miniSidenav: boolean;
  transparentSidenav: boolean;
  sidenavColor: string;
  transparentNavbar: boolean;
  fixedNavbar: boolean;
  openConfigurator: boolean;
  direction: "ltr" | "rtl";
  layout: string;
}

type ActionType =
  | "MINI_SIDENAV"
  | "TRANSPARENT_SIDENAV"
  | "SIDENAV_COLOR"
  | "TRANSPARENT_NAVBAR"
  | "FIXED_NAVBAR"
  | "OPEN_CONFIGURATOR"
  | "DIRECTION"
  | "LAYOUT";

interface Action {
  type: ActionType;
  value: ControllerState[keyof ControllerState];
}

// The Vision UI Dashboard PRO Material main context
const VisionUIState = createContext<ControllerState | null>(null);
const VisionUIDispatch = createContext<Dispatch<Action> | null>(null);

// Setting custom name for the context which is visible on react dev tools
VisionUIState.displayName = "VisionUIStateContext";
VisionUIDispatch.displayName = "VisionUIDispatchContext";

const initialState: ControllerState = {
  miniSidenav: false,
  transparentSidenav: false,
  sidenavColor: "info",
  transparentNavbar: true,
  fixedNavbar: true,
  openConfigurator: false,
  direction: "ltr",
  layout: "dashboard",
};

// Every action is a plain `{ key: value }` assignment, so one table replaces the
// eight identical switch branches this used to be.
const ACTIONS: Record<ActionType, keyof ControllerState> = {
  MINI_SIDENAV: "miniSidenav",
  TRANSPARENT_SIDENAV: "transparentSidenav",
  SIDENAV_COLOR: "sidenavColor",
  TRANSPARENT_NAVBAR: "transparentNavbar",
  FIXED_NAVBAR: "fixedNavbar",
  OPEN_CONFIGURATOR: "openConfigurator",
  DIRECTION: "direction",
  LAYOUT: "layout",
};

// Vision UI Dashboard PRO React reducer
function reducer(state: ControllerState, action: Action): ControllerState {
  const key = ACTIONS[action.type];

  if (!key) {
    throw new Error(`Unhandled action type: ${action.type}`);
  }

  // Bail out when nothing moved: DashboardLayout dispatches LAYOUT on every mount.
  if (state[key] === action.value) {
    return state;
  }

  return { ...state, [key]: action.value };
}

// Vision UI Dashboard PRO React context provider
function VisionUIControllerProvider({ children }: { children: ReactNode }) {
  const [controller, dispatch] = useReducer(reducer, initialState);

  return (
    <VisionUIDispatch.Provider value={dispatch}>
      <VisionUIState.Provider value={controller}>{children}</VisionUIState.Provider>
    </VisionUIDispatch.Provider>
  );
}

// Vision UI Dashboard PRO React custom hook for using context
function useVisionUIController(): [ControllerState, Dispatch<Action>] {
  const controller = useVisionUIState();
  const dispatch = useVisionUIDispatch();

  return useMemo(() => [controller, dispatch], [controller, dispatch]);
}

function useVisionUIState(): ControllerState {
  const controller = useContext(VisionUIState);

  if (!controller) {
    throw new Error("useVisionUIState should be used inside the VisionUIControllerProvider.");
  }

  return controller;
}

function useVisionUIDispatch(): Dispatch<Action> {
  const dispatch = useContext(VisionUIDispatch);

  if (!dispatch) {
    throw new Error("useVisionUIDispatch should be used inside the VisionUIControllerProvider.");
  }

  return dispatch;
}

type Dispatcher = Dispatch<Action>;

// Context module functions
const setMiniSidenav = (dispatch: Dispatcher, value: boolean) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch: Dispatcher, value: boolean) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setSidenavColor = (dispatch: Dispatcher, value: string) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch: Dispatcher, value: boolean) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch: Dispatcher, value: boolean) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch: Dispatcher, value: boolean) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch: Dispatcher, value: ControllerState["direction"]) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch: Dispatcher, value: string) => dispatch({ type: "LAYOUT", value });

export {
  VisionUIControllerProvider,
  useVisionUIController,
  useVisionUIState,
  useVisionUIDispatch,
  setMiniSidenav,
  setTransparentSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
};
